import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import civicApi from '../services/api/civic';
const CivicTenantContext = createContext(null);
export const CivicTenantProvider = ({ children }) => {
    const [tenant, setTenant] = useState(null);
    const [jurisdictions, setJurisdictions] = useState([]);
    const [actor, setActor] = useState(null);
    const [error, setError] = useState(null);
    const refreshJurisdictions = useCallback(async () => {
        const result = await civicApi.jurisdictions();
        setJurisdictions(result.jurisdictions || []);
    }, []);
    const refreshActor = useCallback(async () => {
        setActor(await civicApi.actor());
    }, []);
    useEffect(() => {
        let active = true;
        void Promise.all([civicApi.tenant(), civicApi.jurisdictions(), civicApi.actor()])
            .then(([tenantResult, jurisdictionResult, actorResult]) => {
            if (!active)
                return;
            setTenant(tenantResult.tenant);
            setJurisdictions(jurisdictionResult.jurisdictions || []);
            setActor(actorResult);
        })
            .catch((loadError) => {
            if (active)
                setError(loadError instanceof Error ? loadError.message : 'Unable to load civic tenant configuration.');
        });
        return () => { active = false; };
    }, []);
    const value = useMemo(() => tenant && actor ? {
        tenant,
        jurisdictions,
        actor,
        hasRole: (...roles) => roles.some((role) => actor.roles.includes(role)),
        refreshJurisdictions,
        refreshActor,
    } : null, [actor, jurisdictions, refreshActor, refreshJurisdictions, tenant]);
    if (error)
        return React.createElement("div", { className: "alert alert-danger" }, error);
    if (!value)
        return React.createElement(LoadingSpinner, { message: "Loading civic workspace..." });
    const style = {
        '--civic-primary': tenant.branding.primaryColor,
        '--civic-accent': tenant.branding.accentColor,
        '--civic-surface': tenant.branding.surfaceColor,
        ...(tenant.branding.fontFamily ? { '--civic-font': tenant.branding.fontFamily } : {}),
    };
    return React.createElement(CivicTenantContext.Provider, { value: value },
        React.createElement("div", { className: "wt-civic-tenant", style: style }, children));
};
export function useCivicTenant() {
    const value = useContext(CivicTenantContext);
    if (!value)
        throw new Error('useCivicTenant must be used within CivicTenantProvider');
    return value;
}
